import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BarChart3, Users, ClipboardList, TrendingUp, Clock, CheckCircle,
  XCircle, ArrowUpRight, ChevronRight, Building2, Briefcase, Trophy,
  PieChart, Target, Calendar, Download, Filter, Search, ArrowLeft,
  Menu, X, LogOut, Crown, Star, CreditCard, UserPlus, LayoutDashboard
} from 'lucide-react';
import axios from 'axios';
import { CompanyBrand } from '../components/company/CompanyBrand';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanyAnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    
    const company = localStorage.getItem('company_data');
    if (company) {
      setCompanyData(JSON.parse(company));
    }
    
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashRes, assessRes] = await Promise.all([
        axios.get(`${API}/api/company/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/company/assessments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDashboardData(dashRes.data);
      setCompanyData(dashRes.data.company);
      setAssessments(assessRes.data.assessments || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('company_token');
        navigate('/company/auth');
      }
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('company_token');
    localStorage.removeItem('company_data');
    navigate('/company/auth');
    toast.success('Logged out successfully');
  };

  // Navigation items for sidebar
  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/company/dashboard' },
    { label: 'Job Postings', icon: Briefcase, path: '/company/jobs' },
    { label: 'Assessments', icon: ClipboardList, path: '/company/assessments' },
    { label: 'Hiring Challenges', icon: Trophy, path: '/company/hiring-challenges' },
    { label: 'Enterprise', icon: Crown, path: '/company/enterprise' },
    { label: 'Events', icon: Calendar, path: '/company/events' },
    { label: 'Candidate Pool', icon: UserPlus, path: '/company/candidates' },
    { label: 'Talent Search', icon: Search, path: '/company/talent-search' },
    { label: 'Shortlisted', icon: Star, path: '/company/shortlisted' },
    { label: 'Analytics', icon: BarChart3, path: '/company/analytics', active: true },
    { label: 'Subscription', icon: CreditCard, path: '/company/subscription' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3f2ef]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#0a66c2] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-[#666666]">Loading Analytics...</span>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const completionRate = stats.total_candidates_invited > 0 
    ? Math.round((stats.total_candidates_completed / stats.total_candidates_invited) * 100) 
    : 0;
  const passRate = stats.total_candidates_completed > 0
    ? Math.round((stats.candidates_passed || 0) / stats.total_candidates_completed * 100)
    : 0;

  // Calculate assessment stats
  const activeAssessments = assessments.filter(a => a.is_active).length;

  return (
    <div className="min-h-screen flex bg-[#f3f2ef]">
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
        w-72 lg:w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-out
        lg:transform-none h-screen overflow-y-auto
        ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 px-5 flex items-center justify-between border-b border-gray-200 sticky top-0 bg-white z-10">
          <Link to="/company" className="flex items-center gap-3">
            <CompanyBrand />
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)} 
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -mr-2"
            data-testid="close-sidebar-btn"
          >
            <X size={20} className="text-[#666666]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3">
          <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider px-3 mb-2">Navigation</p>
          <div className="space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all
                  ${item.active 
                    ? 'bg-[#e8f4f8] text-[#0a66c2] font-medium' 
                    : 'text-[#666666] hover:bg-gray-100 hover:text-[#191919]'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={18} strokeWidth={item.active ? 2.5 : 2} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#b24020] hover:bg-red-50 rounded-lg transition-colors"
            data-testid="company-logout-btn"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -ml-2"
              data-testid="open-sidebar-btn"
            >
              <Menu size={22} className="text-[#666666]" />
            </button>
            <div>
              <p className="text-xs font-medium text-[#666666] uppercase tracking-wider hidden sm:block">Overview</p>
              <h1 className="text-lg sm:text-xl font-semibold text-[#191919]">
                Analytics
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <select 
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2] text-[#191919]"
              data-testid="time-range-select"
            >
              <option value="7d">7 days</option>
              <option value="30d">30 days</option>
              <option value="90d">90 days</option>
              <option value="all">All time</option>
            </select>
            <button 
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#0a66c2] text-white text-sm font-medium rounded-full hover:bg-[#004182] transition-colors"
              data-testid="export-report-btn"
            >
              <Download size={16} />
              <span className="hidden md:inline">Export</span>
            </button>
            <button 
              className="sm:hidden p-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] transition-colors"
              data-testid="export-report-btn-mobile"
            >
              <Download size={18} />
            </button>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          
          {/* Overview Stats */}
          <div className="mb-6 sm:mb-8">
            <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider mb-3">Performance Metrics</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-total-assessments">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-[#f3f2ef] rounded-lg flex items-center justify-center">
                    <ClipboardList size={16} className="text-[#666666]" />
                  </div>
                  <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Assessments</p>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-[#191919]">
                  {stats.total_assessments || 0}
                </p>
                <p className="text-xs sm:text-sm text-[#057642] mt-1 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#057642]" />
                  {activeAssessments} active
                </p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-candidates-invited">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-[#f3f2ef] rounded-lg flex items-center justify-center">
                    <Users size={16} className="text-[#666666]" />
                  </div>
                  <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Invited</p>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-[#191919]">
                  {stats.total_candidates_invited || 0}
                </p>
                <p className="text-xs sm:text-sm text-[#666666] mt-1">
                  {stats.total_candidates_completed || 0} completed
                </p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-completion-rate">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-[#f3f2ef] rounded-lg flex items-center justify-center">
                    <Target size={16} className="text-[#666666]" />
                  </div>
                  <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Completion</p>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-[#0a66c2]">
                  {completionRate}%
                </p>
                <div className="mt-2 h-1.5 bg-[#f3f2ef] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#0a66c2] rounded-full transition-all"
                    style={{ width: `${completionRate}%` }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-pass-rate">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-[#f3f2ef] rounded-lg flex items-center justify-center">
                    <CheckCircle size={16} className="text-[#666666]" />
                  </div>
                  <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Pass Rate</p>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-[#057642]">
                  {passRate}%
                </p>
                <p className="text-xs sm:text-sm text-[#666666] mt-1">
                  {stats.candidates_passed || 0} passed
                </p>
              </div>
            </div>
          </div>

          {/* Assessment Performance Table */}
          <div className="bg-white rounded-xl border border-gray-200 mb-6 sm:mb-8 overflow-hidden">
            <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Performance</p>
                <h3 className="font-semibold text-[#191919]">Assessment Analytics</h3>
              </div>
              <Link 
                to="/company/assessments" 
                className="text-sm text-[#0a66c2] hover:underline flex items-center gap-1 self-start sm:self-auto"
                data-testid="view-all-assessments-link"
              >
                View All <ArrowUpRight size={14} />
              </Link>
            </div>

            {assessments.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#f3f2ef] border-b border-gray-200">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">Assessment</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">Status</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">Invited</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">Completed</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">Rate</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">Avg Score</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {assessments.slice(0, 10).map((assessment) => {
                        const rate = assessment.candidates_invited > 0 
                          ? Math.round((assessment.candidates_completed / assessment.candidates_invited) * 100) 
                          : 0;
                        return (
                          <tr key={assessment.id} className="hover:bg-[#f3f2ef] transition-colors">
                            <td className="px-5 py-4">
                              <div>
                                <p className="font-medium text-[#191919] text-sm">{assessment.title}</p>
                                <p className="text-xs text-[#666666] mt-0.5">
                                  {assessment.total_mcqs || 0} MCQ · {assessment.total_coding_problems || 0} Coding
                                </p>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              {assessment.is_active ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#057642]/10 text-[#057642] text-xs font-medium rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#057642]" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-[#666666] text-xs rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#666666]" />
                                  Inactive
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-4 text-[#191919] font-medium">
                              {assessment.candidates_invited || 0}
                            </td>
                            <td className="px-5 py-4 text-[#191919] font-medium">
                              {assessment.candidates_completed || 0}
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-[#f3f2ef] rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-[#0a66c2] rounded-full"
                                    style={{ width: `${rate}%` }}
                                  />
                                </div>
                                <span className="text-sm text-[#666666]">{rate}%</span>
                              </div>
                            </td>
                            <td className="px-5 py-4 text-[#191919] font-medium">
                              {assessment.average_score || '-'}%
                            </td>
                            <td className="px-5 py-4">
                              <Link 
                                to={`/company/assessments/${assessment.id}/analytics`}
                                className="text-sm text-[#0a66c2] hover:underline flex items-center gap-1"
                                data-testid={`assessment-details-${assessment.id}`}
                              >
                                Details <ChevronRight size={14} />
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                  {assessments.slice(0, 10).map((assessment) => {
                    const rate = assessment.candidates_invited > 0 
                      ? Math.round((assessment.candidates_completed / assessment.candidates_invited) * 100) 
                      : 0;
                    return (
                      <Link 
                        key={assessment.id}
                        to={`/company/assessments/${assessment.id}/analytics`}
                        className="block px-4 py-4 hover:bg-[#f3f2ef] transition-colors"
                        data-testid={`assessment-card-${assessment.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0 mr-3">
                            <p className="font-medium text-[#191919] text-sm truncate">{assessment.title}</p>
                            <p className="text-xs text-[#666666] mt-0.5">
                              {assessment.total_mcqs || 0} MCQ · {assessment.total_coding_problems || 0} Coding
                            </p>
                          </div>
                          {assessment.is_active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#057642]/10 text-[#057642] text-xs font-medium rounded-full flex-shrink-0">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#057642]" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-[#666666] text-xs rounded-full flex-shrink-0">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#666666]" />
                              Inactive
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="bg-[#f3f2ef] rounded-lg p-2">
                            <div className="text-lg font-bold text-[#191919]">{assessment.candidates_invited || 0}</div>
                            <div className="text-[10px] text-[#666666]">Invited</div>
                          </div>
                          <div className="bg-[#f3f2ef] rounded-lg p-2">
                            <div className="text-lg font-bold text-[#191919]">{assessment.candidates_completed || 0}</div>
                            <div className="text-[10px] text-[#666666]">Done</div>
                          </div>
                          <div className="bg-[#e8f4f8] rounded-lg p-2">
                            <div className="text-lg font-bold text-[#0a66c2]">{rate}%</div>
                            <div className="text-[10px] text-[#0a66c2]">Rate</div>
                          </div>
                          <div className="bg-[#057642]/10 rounded-lg p-2">
                            <div className="text-lg font-bold text-[#057642]">{assessment.average_score || '-'}%</div>
                            <div className="text-[10px] text-[#057642]">Avg</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-end mt-3 text-sm text-[#0a66c2] font-medium">
                          View Details <ChevronRight size={16} />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="px-4 sm:px-5 py-12 sm:py-16 text-center">
                <ClipboardList className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-[#666666] mb-2">No assessments created yet</p>
                <Link 
                  to="/company/assessments/create" 
                  className="text-sm text-[#0a66c2] hover:underline"
                  data-testid="create-first-assessment-link"
                >
                  Create your first assessment
                </Link>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="mb-6 sm:mb-8">
            <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider mb-3">Quick Access</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <Link 
                to="/company/jobs"
                className="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-[#0a66c2]/30 transition-all flex items-center gap-3"
                data-testid="quick-link-jobs"
              >
                <div className="w-10 h-10 bg-[#f3f2ef] rounded-lg flex items-center justify-center group-hover:bg-[#e8f4f8] transition-colors flex-shrink-0">
                  <Briefcase size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-[#191919] text-sm">Job Analytics</h4>
                  <p className="text-xs text-[#666666]">View job posting performance</p>
                </div>
                <ChevronRight size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors flex-shrink-0" />
              </Link>

              <Link 
                to="/company/enterprise"
                className="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-[#b24020]/30 transition-all flex items-center gap-3"
                data-testid="quick-link-enterprise"
              >
                <div className="w-10 h-10 bg-[#fff7ed] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Trophy size={18} className="text-[#b24020]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-[#191919] text-sm">Enterprise Challenges</h4>
                  <p className="text-xs text-[#666666]">Challenge analytics & reports</p>
                </div>
                <ChevronRight size={18} className="text-[#666666] group-hover:text-[#b24020] transition-colors flex-shrink-0" />
              </Link>

              <Link 
                to="/company/shortlisted"
                className="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-[#0a66c2]/30 transition-all flex items-center gap-3"
                data-testid="quick-link-shortlisted"
              >
                <div className="w-10 h-10 bg-[#f3f2ef] rounded-lg flex items-center justify-center group-hover:bg-[#e8f4f8] transition-colors flex-shrink-0">
                  <Users size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-[#191919] text-sm">Shortlisted Candidates</h4>
                  <p className="text-xs text-[#666666]">View your talent pipeline</p>
                </div>
                <ChevronRight size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors flex-shrink-0" />
              </Link>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default CompanyAnalyticsPage;
